/**
 * RTWO Cryptography & Identity Engine — In-Memory Key Store
 *
 * Stores private key material in-process by reference only.
 * Truthful Data Policy: empty until explicit key generation requests.
 */

import type { CryptoAlgorithm, EllipticCurve, KeyPairId, KeyStatus } from '../../domain-models';

export interface StoredKeyMaterial {
  keyPairId: KeyPairId;
  privateKey: Uint8Array;
  publicKeyHex: string;
  algorithm: CryptoAlgorithm;
  curve: EllipticCurve;
  status: KeyStatus;
  createdAt: number;
  rotatedAt: number | null;
}

export class InMemoryKeyStore {
  private readonly keys = new Map<KeyPairId, StoredKeyMaterial>();

  has(keyPairId: KeyPairId): boolean {
    return this.keys.has(keyPairId);
  }

  get(keyPairId: KeyPairId): StoredKeyMaterial | null {
    const material = this.keys.get(keyPairId);
    return material ? { ...material, privateKey: new Uint8Array(material.privateKey) } : null;
  }

  save(material: StoredKeyMaterial): void {
    this.keys.set(material.keyPairId, {
      ...material,
      privateKey: new Uint8Array(material.privateKey),
    });
  }

  updateStatus(keyPairId: KeyPairId, status: KeyStatus, rotatedAt?: number | null): void {
    const material = this.keys.get(keyPairId);
    if (material) {
      this.keys.set(keyPairId, {
        ...material,
        status,
        rotatedAt: rotatedAt ?? material.rotatedAt,
      });
    }
  }

  clear(): void {
    this.keys.clear();
  }

  count(): number {
    return this.keys.size;
  }
}
